import React, { useState, useEffect } from 'react';
import { Card } from '../../components/Card';
import { Loading } from '../../components/Loading';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Clock, AlertTriangle, CheckCircle, Mail, MessageSquare, Phone, Globe, Download } from 'lucide-react';
import { exportService } from '../../services/exportService';
import { apiClient } from '../../services/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface AgentPerformance {
  agentId: string;
  name: string;
  email: string;
  avatarUrl: string;
  status: string;
  assignedTickets: number;
  resolvedToday: number;
  resolvedThisWeek: number;
  resolvedThisMonth: number;
  averageResponseTimeHours: number;
  averageResolutionTimeHours: number;
  customerSatisfactionScore: number;
  satisfactionSurveyCount: number;
  firstContactResolutionRate: number;
  slaComplianceRate: number;
  productivityScore: number;
  utilizationRate: number;
  skillTags: string[];
  lastActivity: string;
}

interface OperationalDashboard {
  summary: {
    openTickets: number;
    pendingTickets: number;
    overdueTickets: number;
    unassignedTickets: number;
    escalatedTickets: number;
    averageWaitTime: number;
    activeAgents: number;
    availableAgents: number;
    teamCapacityUtilization: number;
  };
  agentPerformance: AgentPerformance[];
  queueStatus: {
    queues: Array<{
      queueName: string;
      totalTickets: number;
      newTickets: number;
      inProgressTickets: number;
      pendingTickets: number;
      overdueTickets: number;
      averageWaitTime: number;
      oldestTicketDate: string;
    }>;
    priorityDistribution: {
      critical: number;
      urgent: number;
      high: number;
      medium: number;
      low: number;
    };
    categoryBreakdown: Array<{
      category: string;
      count: number;
      percentage: number;
      averageResolutionTime: number;
    }>;
  };
  channelMetrics: Array<{
    channel: string;
    totalTickets: number;
    resolvedTickets: number;
    averageResponseTime: number;
    averageResolutionTime: number;
    customerSatisfactionScore: number;
    conversionRate: number;
    volumeTrend: Array<{ date: string; value: number; label: string }>;
  }>;
  workloadDistribution: {
    agentWorkloads: Array<{
      agentId: string;
      name: string;
      assignedTickets: number;
      capacityLimit: number;
      utilizationPercentage: number;
      status: string;
    }>;
    averageWorkload: number;
    workloadStandardDeviation: number;
    overloadedAgentsCount: number;
    underUtilizedAgentsCount: number;
  };
  slaStatus: Array<{
    priority: string;
    metricType: string;
    totalTickets: number;
    ticketsOnTrack: number;
    ticketsAtRisk: number;
    ticketsBreached: number;
    compliancePercentage: number;
    averageTimeToTarget: string;
  }>;
}

const OperationalDashboard: React.FC = () => {
  useDocumentTitle('Operational Dashboard | SupportDesk');
  const [dashboard, setDashboard] = useState<OperationalDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({ from: '7d', to: 'now' });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedAgent]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        fromDate: getDateFromRange(dateRange.from),
        toDate: getDateFromRange(dateRange.to)
      });

      if (selectedAgent) {
        params.append('agentId', selectedAgent);
      }

      const response = await apiClient.get(`/analytics/operational/dashboard?${params.toString()}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching operational dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const fromDate = new Date(getDateFromRange(dateRange.from));
      const toDate = new Date(getDateFromRange(dateRange.to));

      await exportService.exportOperationalDashboard({
        fromDate,
        toDate,
        agentId: selectedAgent || undefined,
      });
    } catch (error) {
      console.error('Export failed:', error);
      setExportError('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getDateFromRange = (range: string): string => {
    const now = new Date();
    if (range === 'now') return now.toISOString();

    const days = parseInt(range.replace('d', ''));
    const date = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return date.toISOString();
  };

  const getChannelIcon = (channel: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'Email': <Mail className="w-5 h-5" />,
      'Chat': <MessageSquare className="w-5 h-5" />,
      'Phone': <Phone className="w-5 h-5" />,
      'Web': <Globe className="w-5 h-5" />
    };
    return iconMap[channel] || <Globe className="w-5 h-5" />;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'Online': 'bg-green-500',
      'Away': 'bg-yellow-500',
      'Busy': 'bg-red-500',
      'Offline': 'bg-gray-500'
    };
    return colorMap[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" text="Loading operational dashboard..." />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Dashboard Unavailable</h2>
          <p className="text-gray-600">Unable to load operational dashboard data.</p>
        </Card>
      </div>
    );
  }

  const priorityData = [
    { name: 'Critical', value: dashboard.queueStatus.priorityDistribution.critical, color: '#EF4444' },
    { name: 'Urgent', value: dashboard.queueStatus.priorityDistribution.urgent, color: '#F59E0B' },
    { name: 'High', value: dashboard.queueStatus.priorityDistribution.high, color: '#3B82F6' },
    { name: 'Medium', value: dashboard.queueStatus.priorityDistribution.medium, color: '#10B981' },
    { name: 'Low', value: dashboard.queueStatus.priorityDistribution.low, color: '#8B5CF6' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Operational Dashboard</h1>
          <p className="text-gray-600">Real-time support operations overview</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            aria-label="Filter by agent"
            value={selectedAgent || ''}
            onChange={(e) => setSelectedAgent(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Agents</option>
            {dashboard.agentPerformance.map(agent => (
              <option key={agent.agentId} value={agent.agentId}>
                {agent.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Reporting period"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1d">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>

          <Button
            onClick={fetchDashboardData}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>

          <Button
            onClick={handleExport}
            variant="primary"
            size="sm"
            disabled={isExporting || loading}
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>
      {exportError && <p role="alert" className="text-sm text-red-700">{exportError}</p>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="text-blue-600">
              <CheckCircle className="w-8 h-8" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-gray-900">{dashboard.summary.openTickets}</div>
            <div className="text-sm text-gray-600">Open Tickets</div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="text-yellow-600">
              <Clock className="w-8 h-8" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-gray-900">{dashboard.summary.overdueTickets}</div>
            <div className="text-sm text-gray-600">Overdue Tickets</div>
          </div>
        </Card>

        <Card className="p-6">
          <Users className="w-8 h-8 text-green-600" />
          <div className="mt-4">
            <div className="text-2xl font-bold text-gray-900">{dashboard.summary.activeAgents}</div>
            <div className="text-sm text-gray-600">Active Agents</div>
          </div>
        </Card>


      </div>

      {/* Priority Distribution and Channel Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Distribution */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Ticket Priority Distribution</h3>
          <div className="flex items-center justify-between">
            {priorityData.some(item => item.value > 0) ? <ResponsiveContainer width="60%" height={200}>
              <PieChart>
                <Pie
                  data={priorityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  dataKey="value"
                >
                  {priorityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer> : <p className="text-sm text-gray-500">No tickets in this period</p>}

            <div className="space-y-2">
              {priorityData.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium">{item.name}</span>
                  <span className="text-sm text-gray-600">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Channel Performance */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Channel Performance</h3>
          <div className="space-y-4">
            {dashboard.channelMetrics.map((channel, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-gray-600">
                    {getChannelIcon(channel.channel)}
                  </div>
                  <div>
                    <div className="font-medium">{channel.channel}</div>
                    <div className="text-sm text-gray-600">
                      {channel.totalTickets} tickets
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-medium">{channel.totalTickets > 0 ? `${channel.averageResponseTime.toFixed(1)}h avg response` : 'No responses'}</div>
                </div>
              </div>
            ))}
            {dashboard.channelMetrics.length === 0 && <p className="text-sm text-gray-500">No channel data in this period</p>}
          </div>
        </Card>
      </div>

      {/* Agent Performance Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Agent Performance</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Agent
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Resolved Today
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Avg Response
                </th>


              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboard.agentPerformance.map((agent) => (
                <tr key={agent.agentId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                          {agent.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.assignedTickets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.resolvedToday}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.averageResponseTimeHours.toFixed(1)}h
                  </td>


                </tr>
              ))}
              {dashboard.agentPerformance.length === 0 && <tr><td colSpan={4} className="px-6 py-6 text-center text-sm text-gray-500">No agent activity in this period</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Workload Distribution */}

    </div>
  );
};

export default OperationalDashboard;
